// Creative Document v2 — canonical contract (CREATIVE-MODEL-V1.md, ADR-0018).
//
// Strict: every object rejects unknown keys. Literal values only: no raw
// CSS/HTML/SVG markup, no expression strings, no executable URLs. Ids are
// flat and document-scoped. This module is the STRUCTURAL layer (L1);
// references, interaction, survey, game and policy checks live in validate/.

import { ASSET_KINDS as DOMAIN_ASSET_KINDS, CREATIVE_PRODUCTS } from "@interactads/domain";
import type { IssueSink } from "../issues.js";
import { html5Project } from "./html5.js";
import { ENGINE_LIMITS as L } from "../limits.js";
import {
  FAIL,
  array,
  boolean,
  describeSchema,
  id,
  integer,
  isPlainObject,
  lazy,
  literal,
  number,
  object,
  oneOf,
  optional,
  record,
  refine,
  string,
  union,
  type Infer,
  type Schema,
} from "./dsl.js";

export const CREATIVE_DOCUMENT_KIND = "interactads.creative" as const;
export const CREATIVE_DOCUMENT_SCHEMA_VERSION = 2 as const;

// ─── Scalars ────────────────────────────────────────────────────────────────

const px = number({ decimals: 2, min: -L.coordinateMax, max: L.coordinateMax });
const size = number({ decimals: 2, min: 0, max: L.coordinateMax });
const ms = integer({ min: 0, max: L.durationMaxMs });
const unit = number({ min: 0, max: 1 });
export const COLOR_PATTERN = /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/;
const color = string({ min: 1, max: 9, pattern: COLOR_PATTERN, patternName: "color #RRGGBB[AA]" });
/** Same pattern as distributions.format in the API (apps/api schemas). */
export const FORMAT_KEY_PATTERN = /^[A-Za-z0-9_.:x-]{1,64}$/;
const tag = string({ min: 1, max: 32, pattern: /^[a-z][a-z0-9_-]{0,31}$/, patternName: "tag" });
const shortText = (max: number) => string({ min: 0, max });
const label = string({ min: 1, max: 200 });

function nullable<T>(schema: Schema<T>): Schema<T | null> {
  return {
    parse: (value, path, sink) => (value === null ? null : schema.parse(value, path, sink)),
    describe: () => ({ anyOf: [describeSchema(schema), { type: "null" }] }),
  };
}

/** Plain JSON object (no functions, no non-finite numbers); content checked by its owner (component registry). */
export type JsonValue = null | boolean | number | string | readonly JsonValue[] | { readonly [key: string]: JsonValue };
export type JsonObject = { readonly [key: string]: JsonValue };
const jsonObject: Schema<JsonObject> = {
  describe: () => ({
    type: "object",
    description: "JSON object; its content is checked by its owner (component registry)",
  }),
  parse(value, path, sink) {
    if (!isPlainObject(value)) {
      sink.add("INVALID_TYPE", path, "Expected object.", { expected: "object" });
      return FAIL;
    }
    return value as JsonObject;
  },
};

// ─── URLs ───────────────────────────────────────────────────────────────────

// Control characters are matched on purpose: they are rejected.
// eslint-disable-next-line no-control-regex
const URL_FORBIDDEN_CHARS = /[\s{}<>"\\^`|\u0000-\u001f\u007f]/;

/** Landing URLs: absolute https only, no credentials, no executable or opaque schemes. */
export function checkLandingUrl(value: string): "ok" | "UNSAFE_URL" | "INVALID_FORMAT" {
  if (value.length > L.urlLength || URL_FORBIDDEN_CHARS.test(value)) return "INVALID_FORMAT";
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return "INVALID_FORMAT";
  }
  if (url.protocol !== "https:") return "UNSAFE_URL";
  if (url.username !== "" || url.password !== "" || url.hostname === "") return "UNSAFE_URL";
  return "ok";
}

const landingUrl: Schema<string> = {
  describe: () => ({
    type: "string",
    maxLength: L.urlLength,
    pattern: "^https://",
    description: 'absolute https URL, no credentials, no spaces or {}<>"\\^`| characters',
  }),
  parse(value, path, sink) {
    if (typeof value !== "string") {
      sink.add("INVALID_TYPE", path, "Expected string.", { expected: "string" });
      return FAIL;
    }
    const verdict = checkLandingUrl(value);
    if (verdict === "UNSAFE_URL") {
      sink.add("UNSAFE_URL", path, "Landing URLs must be absolute https URLs without credentials.", {});
      return FAIL;
    }
    if (verdict === "INVALID_FORMAT") {
      sink.add("INVALID_FORMAT", path, "Not a valid absolute URL.", { format: "url" });
      return FAIL;
    }
    return value;
  },
};

// ─── Easing, text, style ────────────────────────────────────────────────────

export const EASING_NAMES = ["linear", "ease", "easeIn", "easeOut", "easeInOut"] as const;
export type Easing =
  | (typeof EASING_NAMES)[number]
  | { readonly cubicBezier: readonly [number, number, number, number] }
  | { readonly steps: number; readonly position: "start" | "end" }
  | { readonly spring: { readonly stiffness: number; readonly damping: number; readonly mass: number } };

const easingNamed = oneOf(EASING_NAMES);
const cubic = object({
  cubicBezier: refine(
    array(number({ min: -10, max: 10, decimals: 4 }), { min: 4, max: 4 }),
    (v, p, s) => {
      const [x1, , x2] = v;
      if (x1 === undefined || x2 === undefined || x1 < 0 || x1 > 1 || x2 < 0 || x2 > 1) {
        s.add("OUT_OF_RANGE", p, "Bezier x control points must be within 0..1.", {});
        return false;
      }
      return true;
    },
    { description: "[x1, y1, x2, y2]; x1 and x2 within 0..1" }
  ),
});
const steps = object({ steps: integer({ min: 1, max: 100 }), position: oneOf(["start", "end"]) });
const spring = object({
  spring: object({
    stiffness: number({ min: 1, max: 1_000 }),
    damping: number({ min: 0, max: 1_000 }),
    mass: number({ min: 0.1, max: 100 }),
  }),
});
export const easing: Schema<Easing> = {
  describe: () => ({
    anyOf: [describeSchema(easingNamed), describeSchema(cubic), describeSchema(steps), describeSchema(spring)],
  }),
  parse(value, path, sink) {
    if (typeof value === "string") return easingNamed.parse(value, path, sink);
    if (isPlainObject(value)) {
      if (Object.hasOwn(value, "cubicBezier")) return cubic.parse(value, path, sink) as Easing | typeof FAIL;
      if (Object.hasOwn(value, "steps")) return steps.parse(value, path, sink);
      if (Object.hasOwn(value, "spring")) return spring.parse(value, path, sink);
    }
    sink.add("UNSUPPORTED_KIND", path, "Unsupported easing.", {
      allowed: `${EASING_NAMES.join("|")}|cubicBezier|steps|spring`,
    });
    return FAIL;
  },
};

export const TEXT_BIND_FORMATS = ["integer", "decimal1", "mmss"] as const;
const textRunPlain = object({
  text: shortText(L.textRunLength),
  style: optional(
    object({
      weight: optional(integer({ min: 100, max: 900 })),
      italic: optional(boolean()),
      underline: optional(boolean()),
      color: optional(color),
    })
  ),
});
const textRunBind = object({ bind: object({ var: id(), format: oneOf(TEXT_BIND_FORMATS) }) });
export type TextRun = Infer<typeof textRunPlain> | Infer<typeof textRunBind>;
const textRun: Schema<TextRun> = {
  describe: () => ({ anyOf: [describeSchema(textRunPlain), describeSchema(textRunBind)] }),
  parse: (value, path, sink) =>
    isPlainObject(value) && Object.hasOwn(value, "bind")
      ? textRunBind.parse(value, path, sink)
      : textRunPlain.parse(value, path, sink),
};
export const textContent = object({ runs: array(textRun, { min: 1, max: L.textRuns }) });
export type TextContent = Infer<typeof textContent>;

const fontWeight = refine(
  integer({ min: 100, max: 900 }),
  (v, p, s) => {
    if (v % 100 !== 0) {
      s.add("INVALID_VALUE", p, "Font weight must be a multiple of 100.", {});
      return false;
    }
    return true;
  },
  { multipleOf: 100 }
);
const typographyFields = {
  font: optional(id()),
  weight: optional(fontWeight),
  italic: optional(boolean()),
  lineHeight: optional(number({ min: 0.5, max: 4, decimals: 3 })),
  letterSpacing: optional(number({ min: -20, max: 100, decimals: 2 })),
  /** "justify" (7.6D.1, additive): lines fill the width; the last line aligns to the start. */
  align: optional(oneOf(["left", "center", "right", "justify"])),
  verticalAlign: optional(oneOf(["top", "middle", "bottom"])),
  color: optional(color),
  transform: optional(oneOf(["none", "uppercase", "lowercase"])),
};
const fontSize = number({ min: 1, max: 500, decimals: 2 });
export const typography = object({ size: fontSize, ...typographyFields });
const typographyPatch = object({ size: optional(fontSize), ...typographyFields });
export type Typography = Infer<typeof typography>;

const gradientStops = array(object({ at: unit, color }), { min: 2, max: L.gradientStops });
export const fill = union("kind", {
  none: object({ kind: literal("none") }),
  solid: object({ kind: literal("solid"), color }),
  linear: object({
    kind: literal("linear"),
    angle: optional(number({ min: 0, max: 360, decimals: 2 })),
    stops: gradientStops,
  }),
  radial: object({ kind: literal("radial"), stops: gradientStops }),
});
export type Fill = Infer<typeof fill>;

const radius: Schema<number | readonly number[]> = {
  describe: () => ({
    anyOf: [describeSchema(size), describeSchema(array(size, { min: 4, max: 4 }))],
  }),
  parse(value, path, sink) {
    if (Array.isArray(value)) return array(size, { min: 4, max: 4 }).parse(value, path, sink);
    return size.parse(value, path, sink);
  },
};
const border = object({ width: size, color, style: oneOf(["solid", "dashed", "dotted"]) });
const shadow = object({ x: px, y: px, blur: size, spread: optional(px), color });
export const boxStyle = object({
  fill: optional(fill),
  border: optional(border),
  radius: optional(radius),
  shadows: optional(array(shadow, { max: L.shadows })),
  blendMode: optional(oneOf(["normal", "multiply", "screen", "overlay"])),
});
export type BoxStyle = Infer<typeof boxStyle>;

export const IMAGE_FITS = ["cover", "contain", "fill", "none"] as const;
const imageFit = oneOf(IMAGE_FITS);

// ─── Geometry ───────────────────────────────────────────────────────────────

export const frame = object({
  x: px,
  y: px,
  width: size,
  height: size,
  rotation: optional(number({ min: -360, max: 360, decimals: 2 })),
  origin: optional(object({ x: unit, y: unit })),
});
export type Frame = Infer<typeof frame>;
export const constraints = object({
  h: oneOf(["left", "right", "leftRight", "center", "scale"]),
  v: oneOf(["top", "bottom", "topBottom", "center", "scale"]),
  aspectLock: optional(boolean()),
});
const point = object({ x: px, y: px });

// ─── Animation ──────────────────────────────────────────────────────────────

export const animatableProps = object({
  offsetX: optional(px),
  offsetY: optional(px),
  scaleX: optional(number({ min: -100, max: 100, decimals: 4 })),
  scaleY: optional(number({ min: -100, max: 100, decimals: 4 })),
  rotation: optional(number({ min: -3_600, max: 3_600, decimals: 2 })),
  opacity: optional(unit),
});
export type AnimatableProps = Infer<typeof animatableProps>;

const iterations: Schema<number | "infinite"> = {
  describe: () => ({ anyOf: [{ type: "string", const: "infinite" }, describeSchema(integer({ min: 1, max: 1_000 }))] }),
  parse: (value, path, sink) =>
    value === "infinite" ? "infinite" : integer({ min: 1, max: 1_000 }).parse(value, path, sink),
};
const presetParam: Schema<number | string> = {
  describe: () => ({ anyOf: [{ type: "number" }, describeSchema(string({ max: 64 }))] }),
  parse: (value, path, sink) =>
    typeof value === "number" ? number().parse(value, path, sink) : string({ max: 64 }).parse(value, path, sink),
};
export const ANIMATION_PHASES = ["entrance", "emphasis", "exit", "custom"] as const;
export const animationTrack = object({
  id: id(),
  phase: oneOf(ANIMATION_PHASES),
  start: union("mode", {
    timeline: object({ mode: literal("timeline"), at: ms }),
    manual: object({ mode: literal("manual") }),
  }),
  delay: optional(ms),
  duration: integer({ min: 1, max: L.durationMaxMs }),
  iterations: optional(iterations),
  direction: optional(oneOf(["normal", "reverse", "alternate"])),
  easing,
  keyframes: array(object({ at: unit, props: animatableProps, easing: optional(easing) }), {
    min: 2,
    max: L.keyframesPerTrack,
  }),
  /** Provenance only: presets are expanded to keyframes by editor/AI and never resolved at runtime. */
  preset: optional(
    object({
      id: string({ min: 1, max: 64, pattern: /^[a-z][A-Za-z0-9_-]*$/ }),
      version: integer({ min: 1, max: 1_000 }),
      params: optional(record(presetParam, { max: 8 })),
    })
  ),
});
export type AnimationTrack = Infer<typeof animationTrack>;

export const statePatch = object({
  opacity: optional(unit),
  fill: optional(fill),
  border: optional(border),
  shadows: optional(array(shadow, { max: L.shadows })),
  scale: optional(number({ min: 0, max: 100, decimals: 4 })),
  rotation: optional(number({ min: -3_600, max: 3_600, decimals: 2 })),
  offset: optional(point),
  visible: optional(boolean()),
  asset: optional(id()),
  text: optional(textContent),
  transition: optional(object({ duration: ms, easing })),
});
export type StatePatch = Infer<typeof statePatch>;

// ─── Behaviors (game) ───────────────────────────────────────────────────────

export const behaviorUse = union("type", {
  draggable: object({
    type: literal("draggable"),
    axis: optional(oneOf(["x", "y", "both"])),
    bounds: oneOf(["layout", "parent"]),
    snapBack: optional(boolean()),
    dropTargets: optional(array(id(), { max: 16, unique: true })),
  }),
  move: object({
    type: literal("move"),
    velocity: optional(object({ x: number({ min: -10_000, max: 10_000 }), y: number({ min: -10_000, max: 10_000 }) })),
    path: optional(array(point, { min: 2, max: 64 })),
    speed: optional(number({ min: 1, max: 10_000 })),
    loop: optional(boolean()),
    bounce: optional(boolean()),
    despawnOutside: optional(boolean()),
  }),
  collider: object({ type: literal("collider"), shape: oneOf(["rect", "ellipse"]), inset: optional(size) }),
  "follow-pointer": object({
    type: literal("follow-pointer"),
    axis: optional(oneOf(["x", "y", "both"])),
    smoothing: optional(unit),
  }),
  "tappable-target": object({ type: literal("tappable-target"), hitSlop: optional(size) }),
});
export type BehaviorUse = Infer<typeof behaviorUse>;

// ─── Media intent ───────────────────────────────────────────────────────────

const loop: Schema<boolean | { readonly count: number }> = {
  describe: () => ({ anyOf: [{ type: "boolean" }, describeSchema(object({ count: integer({ min: 1, max: 100 }) }))] }),
  parse: (value, path, sink) =>
    typeof value === "boolean" ? value : object({ count: integer({ min: 1, max: 100 }) }).parse(value, path, sink),
};
export const playbackIntent = object({
  autoplay: oneOf(["none", "muted", "withSound"]),
  loop: optional(loop),
  controls: optional(oneOf(["none", "minimal", "native"])),
  startMuted: optional(boolean()),
  preload: oneOf(["none", "metadata", "auto", "onInteraction"]),
  endBehavior: optional(oneOf(["hold", "poster", "hide"])),
  /**
   * Initial volume 0..1 once sound is allowed (7.6E, additive). Never unmutes by itself: sound
   * still needs a user gesture (RUNTIME-CONTRACT §7); default 1.
   */
  volume: optional(unit),
});
export type PlaybackIntent = Infer<typeof playbackIntent>;

// ─── Elements ───────────────────────────────────────────────────────────────

const elementBase = {
  id: id(),
  name: optional(string({ min: 1, max: 120 })),
  frame,
  constraints: optional(constraints),
  visible: optional(boolean()),
  opacity: optional(unit),
  timing: optional(object({ start: optional(ms), end: optional(ms) })),
  style: optional(boxStyle),
  states: optional(record(statePatch, { max: L.statesPerElement })),
  initialState: optional(id()),
  animations: optional(array(animationTrack, { max: L.tracksPerElement })),
  interactive: optional(
    object({
      cursor: optional(oneOf(["pointer", "default"])),
      focusable: optional(boolean()),
      label: optional(string({ min: 1, max: 200 })),
    })
  ),
  behaviors: optional(array(behaviorUse, { max: L.behaviorsPerElement })),
  tags: optional(array(tag, { max: L.tagsPerElement, unique: true })),
  onlyIn: optional(array(id(), { min: 1, max: L.layouts, unique: true })),
  template: optional(boolean()),
};

const SVG_PATH = /^[MLHVCSQTAZmlhvcsqtaz0-9eE.,+\-\s]*$/;
const shapePath = refine(
  string({ min: 1, max: L.pathLength }),
  (v, p, s) => {
    if (!SVG_PATH.test(v) || !/^\s*[Mm]/.test(v)) {
      s.add(
        "INVALID_FORMAT",
        p,
        "Path must use the restricted vector path grammar (M L H V C S Q T A Z and numbers).",
        {
          format: "path",
        }
      );
      return false;
    }
    return true;
  },
  {
    pattern: "^\\s*[Mm][MLHVCSQTAZmlhvcsqtaz0-9eE.,+\\-\\s]*$",
    description: "vector path: M L H V C S Q T A Z and numbers",
  }
);

export const ELEMENT_TYPES = [
  "text",
  "image",
  "shape",
  "button",
  "video",
  "audio",
  "group",
  "hotspot",
  "field",
  "component",
] as const;
export type ElementType = (typeof ELEMENT_TYPES)[number];

/** Registry reference "ia.<name>@<major>"; only the InteractAds namespace exists (no third-party code). */
export const COMPONENT_REF_PATTERN = /^ia\.[a-z][a-z0-9-]{0,40}@[1-9][0-9]{0,3}$/;

export const element = union("type", {
  text: object({
    ...elementBase,
    type: literal("text"),
    content: textContent,
    typography,
    fit: optional(
      union("mode", {
        none: object({ mode: literal("none") }),
        shrink: object({ mode: literal("shrink"), minSize: fontSize }),
      })
    ),
    maxLines: optional(integer({ min: 1, max: 20 })),
  }),
  image: object({
    ...elementBase,
    type: literal("image"),
    asset: id(),
    fit: imageFit,
    focal: optional(object({ x: unit, y: unit })),
    crop: optional(object({ x: unit, y: unit, w: unit, h: unit })),
    alt: shortText(300),
  }),
  shape: object({
    ...elementBase,
    type: literal("shape"),
    shape: oneOf(["rect", "ellipse", "line", "polygon", "path"]),
    points: optional(array(point, { min: 2, max: L.polygonPoints })),
    path: optional(shapePath),
    stroke: optional(object({ width: size, color })),
  }),
  button: object({
    ...elementBase,
    type: literal("button"),
    label: textContent,
    typography,
    role: oneOf(["cta", "button"]),
  }),
  video: object({
    ...elementBase,
    type: literal("video"),
    asset: id(),
    poster: optional(id()),
    captions: optional(id()),
    fit: imageFit,
    playback: playbackIntent,
  }),
  audio: object({ ...elementBase, type: literal("audio"), asset: id(), playback: playbackIntent }),
  group: object({
    ...elementBase,
    type: literal("group"),
    children: array(id(), { max: L.childrenPerContainer }),
    clip: optional(boolean()),
  }),
  hotspot: object({ ...elementBase, type: literal("hotspot"), shape: oneOf(["rect", "ellipse"]) }),
  field: object({
    ...elementBase,
    type: literal("field"),
    form: id(),
    field: id(),
    /** Look of the runtime-drawn control (7.6E, additive; shared by every variant). */
    appearance: optional(
      object({
        textColor: optional(color),
        accentColor: optional(color),
        fontSize: optional(number({ min: 8, max: 48, decimals: 2 })),
      })
    ),
  }),
  component: object({
    ...elementBase,
    type: literal("component"),
    component: string({ min: 1, max: 64, pattern: COMPONENT_REF_PATTERN, patternName: "ia.<name>@<major>" }),
    props: jsonObject,
    slots: optional(record(array(id(), { max: L.childrenPerContainer }), { max: 8 })),
  }),
});
export type Element = Infer<typeof element>;
export type ElementOf<T extends ElementType> = Extract<Element, { type: T }>;

// ─── Scenes, layouts, overlay ───────────────────────────────────────────────

export const SCENE_ROLES = ["content", "endcard", "survey", "game", "result", "form"] as const;
const sceneBackground = union("kind", {
  none: object({ kind: literal("none") }),
  solid: object({ kind: literal("solid"), color }),
  linear: object({
    kind: literal("linear"),
    angle: optional(number({ min: 0, max: 360, decimals: 2 })),
    stops: gradientStops,
  }),
  radial: object({ kind: literal("radial"), stops: gradientStops }),
  image: object({ kind: literal("image"), asset: id(), fit: imageFit }),
});
const onTimelineEnd: Schema<"hold" | "loop" | { readonly goTo: string }> = {
  describe: () => ({ anyOf: [describeSchema(oneOf(["hold", "loop"])), describeSchema(object({ goTo: id() }))] }),
  parse: (value, path, sink) =>
    typeof value === "string"
      ? oneOf(["hold", "loop"]).parse(value, path, sink)
      : object({ goTo: id() }).parse(value, path, sink),
};
export const sceneTransition = object({
  kind: oneOf(["none", "fade", "slide", "push"]),
  direction: optional(oneOf(["left", "right", "up", "down"])),
  duration: ms,
  easing,
});
export const scene = object({
  id: id(),
  name: optional(string({ min: 1, max: 120 })),
  role: optional(oneOf(SCENE_ROLES)),
  duration: nullable(integer({ min: 1, max: L.durationMaxMs })),
  onTimelineEnd: optional(onTimelineEnd),
  background: optional(sceneBackground),
  children: array(id(), { max: L.childrenPerContainer }),
  childrenByLayout: optional(record(array(id(), { max: L.childrenPerContainer }), { max: L.layouts })),
  transitionIn: optional(sceneTransition),
});
export type Scene = Infer<typeof scene>;

const canvasSide = number({ decimals: 2, min: 1, max: L.canvasMax });
export const layoutOverride = object({
  frame: optional(frame),
  constraints: optional(constraints),
  visible: optional(boolean()),
  typography: optional(typographyPatch),
  fit: optional(imageFit),
  style: optional(boxStyle),
  content: optional(textContent),
  animations: optional(array(animationTrack, { max: L.tracksPerElement })),
  states: optional(record(statePatch, { max: L.statesPerElement })),
});
export type LayoutOverride = Infer<typeof layoutOverride>;

export const layout = object({
  id: id(),
  formatKeys: array(string({ min: 1, max: 64, pattern: FORMAT_KEY_PATTERN, patternName: "format" }), {
    min: 1,
    max: L.formatKeysPerLayout,
    unique: true,
  }),
  sizing: union("mode", {
    fixed: object({ mode: literal("fixed"), width: canvasSide, height: canvasSide }),
    responsive: object({
      mode: literal("responsive"),
      design: object({ width: canvasSide, height: canvasSide }),
      range: object({ minWidth: canvasSide, maxWidth: canvasSide, minHeight: canvasSide, maxHeight: canvasSide }),
      aspect: optional(object({ min: number({ min: 0.01, max: 100 }), max: number({ min: 0.01, max: 100 }) })),
      overflow: literal("clip"),
    }),
  }),
  overrides: optional(record(layoutOverride, { max: L.elements })),
});
export type Layout = Infer<typeof layout>;

// ─── Assets, fonts, links ───────────────────────────────────────────────────

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const SHA256 = /^[0-9a-f]{64}$/;
/** Same list as the control plane asset tables (@interactads/domain). */
export const ASSET_KINDS = DOMAIN_ASSET_KINDS;
export type AssetKind = (typeof ASSET_KINDS)[number];
/** Allowed media types per kind. SVG is deliberately absent: it can carry script and needs a sanitizing pipeline first. */
export const ASSET_MIME_TYPES: Readonly<Record<AssetKind, readonly string[]>> = {
  image: ["image/png", "image/jpeg", "image/webp", "image/avif", "image/gif"],
  video: ["video/mp4", "video/webm"],
  audio: ["audio/mpeg", "audio/mp4", "audio/aac", "audio/webm", "audio/ogg"],
  font: ["font/woff2"],
  captions: ["text/vtt"],
};

export const assetRef = refine(
  object({
    key: id(),
    assetId: string({ min: 36, max: 36, pattern: UUID, patternName: "uuid" }),
    revisionId: string({ min: 36, max: 36, pattern: UUID, patternName: "uuid" }),
    sha256: string({ min: 64, max: 64, pattern: SHA256, patternName: "sha256 hex" }),
    kind: oneOf(ASSET_KINDS),
    mime: string({ min: 1, max: 64 }),
    bytes: integer({ min: 1, max: 1_073_741_824 }),
    width: optional(integer({ min: 1, max: 16_384 })),
    height: optional(integer({ min: 1, max: 16_384 })),
    durationMs: optional(integer({ min: 1, max: 86_400_000 })),
    hasAlpha: optional(boolean()),
    role: optional(oneOf(["content", "poster", "fallback", "font", "captions"])),
  }),
  (v, p, s) => {
    let ok = true;
    if (!ASSET_MIME_TYPES[v.kind].includes(v.mime)) {
      s.add("UNSUPPORTED_MEDIA_TYPE", `${p}.mime`, `Media type not allowed for ${v.kind} assets.`, {
        kind: v.kind,
        allowed: ASSET_MIME_TYPES[v.kind].join("|"),
      });
      ok = false;
    }
    if ((v.kind === "image" || v.kind === "video") && (v.width === undefined || v.height === undefined)) {
      s.add("REQUIRED", `${p}.width`, `${v.kind} assets declare width and height.`, {});
      ok = false;
    }
    if ((v.kind === "video" || v.kind === "audio") && v.durationMs === undefined) {
      s.add("REQUIRED", `${p}.durationMs`, `${v.kind} assets declare durationMs.`, {});
      ok = false;
    }
    return ok;
  },
  { description: "mime must match kind; image/video declare width and height; video/audio declare durationMs" }
);
export type AssetRef = Infer<typeof assetRef>;

export const fontRef = object({
  key: id(),
  asset: id(),
  family: string({ min: 1, max: 100, pattern: /^[A-Za-z0-9 _-]+$/, patternName: "font family" }),
  weights: array(fontWeight, { min: 1, max: 9, unique: true }),
});
export type FontRef = Infer<typeof fontRef>;

// eslint-disable-next-line no-control-regex -- control characters are rejected on purpose
const queryValue = string({ max: 256, pattern: /^[^{}<>"\\\u0000-\u001f\u007f]*$/, patternName: "query value" });
export const link = object({
  id: id(),
  kind: literal("landing"),
  url: landingUrl,
  label: optional(string({ min: 1, max: 120 })),
  appendQuery: optional(record(queryValue, { max: 16 })),
});
export type Link = Infer<typeof link>;

// ─── State, timers, events ──────────────────────────────────────────────────

const reportMode = optional(oneOf(["never", "onComplete"]));
const enumValue = string({ min: 1, max: 32, pattern: /^[A-Za-z0-9_-]{1,32}$/, patternName: "enum value" });
export const variableDecl = union("type", {
  number: object({
    id: id(),
    type: literal("number"),
    initial: number({ min: -1_000_000_000, max: 1_000_000_000 }),
    min: optional(number({ min: -1_000_000_000, max: 1_000_000_000 })),
    max: optional(number({ min: -1_000_000_000, max: 1_000_000_000 })),
    integer: optional(boolean()),
    report: reportMode,
  }),
  boolean: object({ id: id(), type: literal("boolean"), initial: boolean(), report: reportMode }),
  enum: object({
    id: id(),
    type: literal("enum"),
    values: array(enumValue, { min: 1, max: L.enumValues, unique: true }),
    initial: enumValue,
    report: reportMode,
  }),
});
export type VariableDecl = Infer<typeof variableDecl>;

const repeat: Schema<boolean | number> = {
  describe: () => ({ anyOf: [{ type: "boolean" }, describeSchema(integer({ min: 1, max: 10_000 }))] }),
  parse: (value, path, sink) =>
    typeof value === "boolean" ? value : integer({ min: 1, max: 10_000 }).parse(value, path, sink),
};
export const timerDecl = object({
  id: id(),
  durationMs: integer({ min: L.timerMinMs, max: L.durationMaxMs }),
  repeat: optional(repeat),
  autoStart: optional(boolean()),
  pauseWhenHidden: optional(boolean()),
});
export type TimerDecl = Infer<typeof timerDecl>;

export const CUSTOM_EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
export const customEventDecl = object({
  name: string({ min: 1, max: 40, pattern: CUSTOM_EVENT_NAME_PATTERN, patternName: "event name" }),
  label: optional(string({ min: 1, max: 120 })),
  props: optional(
    record(
      union("type", {
        enum: object({
          type: literal("enum"),
          values: array(string({ min: 1, max: 32, pattern: /^[a-z0-9_]{1,32}$/ }), {
            min: 1,
            max: L.customEventEnumValues,
            unique: true,
          }),
        }),
        int: object({
          type: literal("int"),
          min: integer({ min: -1_000_000, max: 1_000_000 }),
          max: integer({ min: -1_000_000, max: 1_000_000 }),
        }),
      }),
      { max: L.customEventProps }
    )
  ),
  maxPerInstance: integer({ min: 1, max: L.customEventMaxPerInstance }),
});
export type CustomEventDecl = Infer<typeof customEventDecl>;

// ─── Expressions (AST — never text) ─────────────────────────────────────────

export type Expr =
  | number
  | boolean
  | string
  | { readonly var: string }
  | { readonly elementState: string }
  | { readonly scene: "current" }
  | { readonly field: string; readonly form: string; readonly prop: "filled" | "valid" }
  | { readonly answered: string; readonly option?: string }
  | { readonly timer: string; readonly prop: "remainingMs" | "running" }
  | { readonly random: { readonly min: number; readonly max: number; readonly int?: boolean } }
  | { readonly op: BinaryOp; readonly args: readonly [Expr, Expr] }
  | { readonly op: "and" | "or"; readonly args: readonly Expr[] }
  | { readonly op: "clamp"; readonly args: readonly [Expr, Expr, Expr] }
  | { readonly op: UnaryOp; readonly arg: Expr };

export const COMPARISON_OPS = ["==", "!=", "<", "<=", ">", ">="] as const;
export const ARITHMETIC_OPS = ["+", "-", "*", "/", "min", "max"] as const;
export const UNARY_OPS = ["not", "round", "floor", "ceil", "abs"] as const;
type BinaryOp = (typeof COMPARISON_OPS)[number] | (typeof ARITHMETIC_OPS)[number];
type UnaryOp = (typeof UNARY_OPS)[number];

export const expr: Schema<Expr> = lazy(() => {
  const literalString = string({ min: 0, max: 64, pattern: /^[A-Za-z0-9_-]*$/, patternName: "enum or state literal" });
  const binary = (op: BinaryOp) => object({ op: literal(op), args: array(expr, { min: 2, max: 2 }) });
  const unary = (op: UnaryOp) => object({ op: literal(op), arg: expr });
  const opNode = union("op", {
    ...Object.fromEntries([...COMPARISON_OPS, ...ARITHMETIC_OPS].map((op) => [op, binary(op)])),
    and: object({ op: literal("and"), args: array(expr, { min: 2, max: 8 }) }),
    or: object({ op: literal("or"), args: array(expr, { min: 2, max: 8 }) }),
    clamp: object({ op: literal("clamp"), args: array(expr, { min: 3, max: 3 }) }),
    ...Object.fromEntries(UNARY_OPS.map((op) => [op, unary(op)])),
  });
  const nodes = {
    var: object({ var: id() }),
    elementState: object({ elementState: id() }),
    scene: object({ scene: literal("current") }),
    field: object({ field: id(), form: id(), prop: oneOf(["filled", "valid"]) }),
    answered: object({ answered: id(), option: optional(id()) }),
    timer: object({ timer: id(), prop: oneOf(["remainingMs", "running"]) }),
    random: object({
      random: object({
        min: number({ min: -1_000_000, max: 1_000_000 }),
        max: number({ min: -1_000_000, max: 1_000_000 }),
        int: optional(boolean()),
      }),
    }),
  } as const;
  return {
    describe: () => ({
      anyOf: [
        { type: "number" },
        { type: "boolean" },
        describeSchema(literalString),
        describeSchema(opNode),
        ...Object.values(nodes).map((n) => describeSchema(n)),
      ],
    }),
    parse(value, path, sink): Expr | typeof FAIL {
      if (typeof value === "number") return number().parse(value, path, sink);
      if (typeof value === "boolean") return value;
      if (typeof value === "string") return literalString.parse(value, path, sink);
      if (isPlainObject(value)) {
        if (Object.hasOwn(value, "op")) return opNode.parse(value, path, sink) as Expr | typeof FAIL;
        for (const [key, schema] of Object.entries(nodes)) {
          if (Object.hasOwn(value, key)) return schema.parse(value, path, sink);
        }
      }
      sink.add("UNSUPPORTED_KIND", path, "Unsupported expression node.", {
        allowed: "literal|var|elementState|scene|field|answered|timer|random|op",
      });
      return FAIL;
    },
  };
});

// ─── Triggers ───────────────────────────────────────────────────────────────

/**
 * Game signals (7.6F): what the game module reports to rules. Closed list; they are
 * rule triggers, NOT measurement events (measurement uses the tx: 1 game_* events).
 */
export const GAME_SIGNALS = [
  "start",
  "hit",
  "miss",
  "match",
  "mismatch",
  "drop",
  "reject",
  "win",
  "lose",
  "timeout",
  // Runner (7.6F.1, additive): the player jumped / ducked, touched a pickup / a hazard or
  // obstacle (target = its template or placed layer), a new environment segment began
  // (target = the segment id).
  "jump",
  "duck",
  "pickup",
  "hazard",
  "segment",
] as const;
export type GameSignal = (typeof GAME_SIGNALS)[number];

const collisionRef = refine(
  object({ id: optional(id()), tag: optional(tag) }),
  (v, p, s) => {
    if ((v.id === undefined) === (v.tag === undefined)) {
      s.add("INVALID_VALUE", p, "Collision side must name exactly one of id or tag.", {});
      return false;
    }
    return true;
  },
  { description: "exactly one of id or tag" }
);
const onTarget = <T extends string>(type: T) => object({ type: literal(type), target: id() });
const onScene = <T extends string>(type: T) => object({ type: literal(type), scene: id() });
const onTimer = <T extends string>(type: T) => object({ type: literal(type), timer: id() });
const onSurvey = <T extends string>(type: T) => object({ type: literal(type), survey: id() });
const onForm = <T extends string>(type: T) => object({ type: literal(type), form: id() });

export const trigger = union("type", {
  "pointer.tap": onTarget("pointer.tap"),
  "pointer.hover.enter": onTarget("pointer.hover.enter"),
  "pointer.hover.leave": onTarget("pointer.hover.leave"),
  "pointer.drag.start": onTarget("pointer.drag.start"),
  "pointer.drag.end": onTarget("pointer.drag.end"),
  "drag.drop": object({ type: literal("drag.drop"), target: id(), onto: optional(id()) }),
  "scene.enter": onScene("scene.enter"),
  "scene.exit": onScene("scene.exit"),
  "scene.timelineEnd": onScene("scene.timelineEnd"),
  "timeline.at": object({ type: literal("timeline.at"), scene: id(), atMs: ms }),
  "timer.elapsed": onTimer("timer.elapsed"),
  "timer.tick": object({
    type: literal("timer.tick"),
    timer: id(),
    everyMs: integer({ min: L.timerTickMinMs, max: L.durationMaxMs }),
  }),
  "variable.changed": object({ type: literal("variable.changed"), var: id() }),
  "media.started": onTarget("media.started"),
  "media.paused": onTarget("media.paused"),
  "media.ended": onTarget("media.ended"),
  "media.blocked": onTarget("media.blocked"),
  "media.progress": object({
    type: literal("media.progress"),
    target: id(),
    milestone: refine(
      integer({ min: 25, max: 75 }),
      (v, p, s) => {
        if (v !== 25 && v !== 50 && v !== 75) {
          s.add("INVALID_VALUE", p, "Milestone must be 25, 50 or 75.", {});
          return false;
        }
        return true;
      },
      { enum: [25, 50, 75] }
    ),
  }),
  "component.event": object({
    type: literal("component.event"),
    target: id(),
    event: string({ min: 1, max: 40, pattern: /^[a-z][A-Za-z0-9]*$/ }),
  }),
  "survey.answered": object({ type: literal("survey.answered"), survey: id(), question: optional(id()) }),
  "survey.completed": onSurvey("survey.completed"),
  "survey.exited": onSurvey("survey.exited"),
  "form.submitted": onForm("form.submitted"),
  "form.invalid": onForm("form.invalid"),
  "collision.enter": object({ type: literal("collision.enter"), a: collisionRef, b: collisionRef }),
  "collision.exit": object({ type: literal("collision.exit"), a: collisionRef, b: collisionRef }),
  /**
   * Something happened in the declared game (7.6F, additive; ADR-0022). `target` (optional =
   * any) is the element involved: the target hit, the second card of a pair, the dropped item.
   */
  "game.event": object({ type: literal("game.event"), event: oneOf(GAME_SIGNALS), target: optional(id()) }),
  "creative.ready": object({ type: literal("creative.ready") }),
  "creative.visibility": object({ type: literal("creative.visibility"), visible: boolean() }),
});
export type Trigger = Infer<typeof trigger>;
export type TriggerType = Trigger["type"];
/** Triggers that carry user activation (popups/landing navigation and sound need one). */
export const GESTURE_TRIGGERS: ReadonlySet<TriggerType> = new Set(["pointer.tap", "drag.drop", "form.submitted"]);

// ─── Actions ────────────────────────────────────────────────────────────────

const actTarget = <T extends string>(type: T) => object({ type: literal(type), target: id() });
const actTimer = <T extends string>(type: T) => object({ type: literal(type), timer: id() });
const actForm = <T extends string>(type: T) => object({ type: literal(type), form: id() });
const actRule = <T extends string>(type: T) => object({ type: literal(type), rule: id() });

export type Action = Infer<typeof actionUnionNonRecursive> | AfterAction;
export interface AfterAction {
  readonly type: "after";
  readonly ms: number;
  readonly do: readonly Action[];
}

const actionUnionNonRecursive = union("type", {
  "scene.goTo": object({ type: literal("scene.goTo"), scene: id(), transition: optional(sceneTransition) }),
  "scene.next": object({ type: literal("scene.next"), transition: optional(sceneTransition) }),
  "scene.prev": object({ type: literal("scene.prev"), transition: optional(sceneTransition) }),
  "link.open": object({ type: literal("link.open"), link: id() }),
  "media.play": actTarget("media.play"),
  "media.pause": actTarget("media.pause"),
  "media.toggle": actTarget("media.toggle"),
  "media.mute": actTarget("media.mute"),
  "media.unmute": actTarget("media.unmute"),
  "media.restart": actTarget("media.restart"),
  "element.show": actTarget("element.show"),
  "element.hide": actTarget("element.hide"),
  "element.toggle": actTarget("element.toggle"),
  "element.setState": object({ type: literal("element.setState"), target: id(), state: id() }),
  /**
   * Moves the element to a random position fully inside `area`'s frame in the current format
   * variant (7.6F, additive). Uses the instance RNG (seeded); runtime state only, never the document.
   */
  "element.relocate": object({ type: literal("element.relocate"), target: id(), area: id() }),
  "animation.play": object({ type: literal("animation.play"), target: id(), track: id() }),
  "animation.stop": object({ type: literal("animation.stop"), target: id(), track: id() }),
  "var.set": object({ type: literal("var.set"), var: id(), value: expr }),
  "var.increment": object({ type: literal("var.increment"), var: id(), by: expr }),
  "var.reset": object({ type: literal("var.reset"), var: id() }),
  "random.choose": object({
    type: literal("random.choose"),
    var: id(),
    from: optional(array(enumValue, { min: 1, max: L.enumValues, unique: true })),
    weights: optional(array(number({ min: 0, max: 1_000_000 }), { min: 1, max: L.enumValues })),
  }),
  "timer.start": actTimer("timer.start"),
  "timer.stop": actTimer("timer.stop"),
  "timer.reset": actTimer("timer.reset"),
  "component.command": object({
    type: literal("component.command"),
    target: id(),
    command: string({ min: 1, max: 40, pattern: /^[a-z][A-Za-z0-9]*$/ }),
    args: optional(object({ index: optional(integer({ min: 0, max: 1_000 })) })),
  }),
  "survey.start": object({ type: literal("survey.start"), survey: id() }),
  "form.submit": actForm("form.submit"),
  "form.reset": actForm("form.reset"),
  spawn: object({
    type: literal("spawn"),
    template: id(),
    at: object({ x: expr, y: expr }),
    poolMax: integer({ min: 1, max: L.spawnPoolMax }),
  }),
  despawn: object({
    type: literal("despawn"),
    target: refine(
      object({ id: optional(id()), other: optional(literal(true)) }),
      (v, p, s) => {
        if ((v.id === undefined) === (v.other === undefined)) {
          s.add("INVALID_VALUE", p, "Despawn target must name exactly one of id or other.", {});
          return false;
        }
        return true;
      },
      { description: "exactly one of id or other" }
    ),
  }),
  "game.start": object({ type: literal("game.start") }),
  /** `auto` (7.6F, additive): win if the declared game's win condition holds, else lose. */
  "game.end": object({ type: literal("game.end"), outcome: oneOf(["win", "lose", "timeout", "neutral", "auto"]) }),
  "game.restart": object({ type: literal("game.restart"), scene: id() }),
  "event.emit": object({
    type: literal("event.emit"),
    name: string({ min: 1, max: 40, pattern: CUSTOM_EVENT_NAME_PATTERN, patternName: "event name" }),
    props: optional(record(expr, { max: L.customEventProps })),
  }),
  "rule.enable": actRule("rule.enable"),
  "rule.disable": actRule("rule.disable"),
});

export const action: Schema<Action> = lazy(() => {
  const after = object({
    type: literal("after"),
    ms: integer({ min: 1, max: L.durationMaxMs }),
    do: array(action, { min: 1, max: L.actionsPerAfter }),
  });
  return {
    describe: () => ({
      anyOf: [...(describeSchema(actionUnionNonRecursive).anyOf ?? []), describeSchema(after)],
      "x-discriminator": "type",
    }),
    parse: (value, path, sink) =>
      isPlainObject(value) && value.type === "after"
        ? after.parse(value, path, sink)
        : actionUnionNonRecursive.parse(value, path, sink),
  };
});
export type ActionType = Action["type"];

export const rule = object({
  id: id(),
  on: trigger,
  if: optional(expr),
  do: array(action, { min: 1, max: L.actionsPerRule }),
  once: optional(boolean()),
  cooldownMs: optional(ms),
  enabled: optional(boolean()),
});
export type Rule = Infer<typeof rule>;

// ─── Survey and forms (no PII in initial V1 — CREATIVE-MODEL §11) ───────────

const nextRef = id(); // a question id or the reserved "END"
const scaleSpec = object({
  min: literal(1),
  max: refine(
    integer({ min: 3, max: 10 }),
    (v, p, s) => {
      if (![3, 5, 7, 10].includes(v)) {
        s.add("INVALID_VALUE", p, "Scale max must be 3, 5, 7 or 10.", {});
        return false;
      }
      return true;
    },
    { enum: [3, 5, 7, 10] }
  ),
  labels: optional(object({ min: textContent, max: textContent })),
});
const questionBase = {
  id: id(),
  category: oneOf(["research", "demographic"]),
  prompt: textContent,
  required: boolean(),
};
export const surveyQuestion = union("kind", {
  single: object({
    ...questionBase,
    kind: literal("single"),
    options: array(object({ id: id(), label: textContent, image: optional(id()), next: optional(nextRef) }), {
      min: 2,
      max: L.surveyOptions,
    }),
    next: optional(nextRef),
    shuffleOptions: optional(boolean()),
  }),
  multi: object({
    ...questionBase,
    kind: literal("multi"),
    options: array(object({ id: id(), label: textContent, image: optional(id()) }), { min: 2, max: L.surveyOptions }),
    maxSelections: integer({ min: 1, max: L.surveyOptions }),
    next: nextRef,
    shuffleOptions: optional(boolean()),
  }),
  scale: object({ ...questionBase, kind: literal("scale"), scale: scaleSpec, next: nextRef }),
});
export type SurveyQuestion = Infer<typeof surveyQuestion>;

export const surveyInstrument = object({
  id: id(),
  researchObjective: string({ min: 1, max: 2_000 }),
  sampleType: literal("non_probability"),
  consent: object({ required: boolean(), sceneId: optional(id()) }),
  startQuestionId: id(),
  questions: array(surveyQuestion, { min: 1, max: L.surveyQuestions }),
  presentation: object({ sceneId: id(), completionSceneId: id(), exitSceneId: optional(id()) }),
});
export type SurveyInstrument = Infer<typeof surveyInstrument>;

const fieldBase = { id: id(), label: textContent, required: boolean() };
const choiceOptions = array(object({ id: id(), label: textContent }), { min: 1, max: L.fieldOptions });
const choiceField = <T extends string>(control: T) =>
  object({ ...fieldBase, control: literal(control), options: choiceOptions, dataClass: literal("choice") });
const localTextField = <T extends string>(control: T) =>
  object({
    ...fieldBase,
    control: literal(control),
    validation: optional(
      object({ minLength: optional(integer({ min: 0, max: 280 })), maxLength: optional(integer({ min: 1, max: 280 })) })
    ),
    dataClass: literal("local_only"),
  });
export const formField = union("control", {
  select: choiceField("select"),
  radio: choiceField("radio"),
  checkbox: choiceField("checkbox"),
  rating: object({ ...fieldBase, control: literal("rating"), scale: scaleSpec, dataClass: literal("choice") }),
  text: localTextField("text"),
  textarea: localTextField("textarea"),
});
export type FormField = Infer<typeof formField>;

export const formDecl = object({
  id: id(),
  fields: array(formField, { min: 1, max: L.fieldsPerForm }),
  submit: object({ mode: literal("events") }),
});
export type FormDecl = Infer<typeof formDecl>;

// ─── Game module (advergaming, 7.6F — ADR-0022, INTERACTION-MODEL §7.5) ─────
//
// One declared game per document, like the survey: DATA, never code. The
// runtime executes the closed mechanics (targets, pairs, drops), keeps score,
// lives and time in DECLARED variables/timers, decides the outcome and reports
// `game.event` triggers to ordinary rules (feedback animations, sounds).

const points = integer({ min: -1_000, max: 1_000 });
export const gameTarget = object({
  element: id(),
  /** correct = an objective/score; wrong = a decoy or the "miss" area (penalty). */
  role: oneOf(["correct", "wrong"]),
  /** Overrides scoring.hit (correct) / scoring.miss (wrong). */
  points: optional(points),
  /** Correct targets only: every tap counts (tap/catch) instead of once; never an objective. */
  repeat: optional(boolean()),
  /** After each hit the target moves to a random position inside this element's frame (same variant). */
  relocate: optional(id()),
  /** Element state applied when a one-time correct target is found. */
  foundState: optional(id()),
});
export type GameTarget = Infer<typeof gameTarget>;

export const gamePairs = object({
  /** Each entry is one pair of DIFFERENT elements that match each other. */
  cards: array(array(id(), { min: 2, max: 2 }), { min: 1, max: L.gamePairs }),
  /** Face-down presentation drawn by the runtime over the card (declared colors, no markup). */
  cover: object({ color, accent: optional(color) }),
  /** How long a wrong pair stays visible before both turn face down again. */
  mismatchMs: integer({ min: 300, max: 5_000 }),
  /** Swap the cards' positions (seeded) when a game starts; frames stay explicit per variant. */
  shuffle: boolean(),
  /** Element state applied to both cards of a matched pair. */
  matchedState: optional(id()),
});
export type GamePairs = Infer<typeof gamePairs>;

export const gameDrops = object({
  /** Draggable items (each declares `draggable.dropTargets` = the zones that accept it). */
  items: array(id(), { min: 1, max: L.gameDropItems, unique: true }),
  /** Every drop zone of the game: a drop on a zone that does not accept the item is a rejection. */
  zones: array(id(), { min: 1, max: L.gameDropZones, unique: true }),
});
export type GameDrops = Infer<typeof gameDrops>;

// ─── Runner / platformer (7.6F.1 — ADR-0023, INTERACTION-MODEL §7.6) ──────
//
// A controlled side-scrolling runner, declared as data inside the SAME game
// module: one ordinary layer is the player (jump / duck with bounded,
// deterministic curves), the world scrolls at a declared speed, background
// layers move at their own factor (parallax, optionally looping), environment
// segments follow each other in time (city → town → beach, day → night as the
// scenery each segment shows), spawn rules create ephemeral instances of
// template layers (hazards, obstacles, pickups) with the seeded instance RNG,
// and axis-aligned hit boxes decide contacts. Spawned instances, positions and
// poses live only in the game session: the document never changes. No physics,
// no code. Sizes are relative to the player's own explicit frame in each
// format; world speed is px/s in the active format (explicit per format).

export const RUNNER_OBJECT_KINDS = ["hazard", "obstacle", "pickup"] as const;
export type RunnerObjectKind = (typeof RUNNER_OBJECT_KINDS)[number];

/** What touching the object does (hazard/obstacle: ≤ 0 values; pickup: ≥ 0). */
export const runnerEffect = object({
  energy: optional(integer({ min: -1_000, max: 1_000 })),
  score: optional(points),
  lives: optional(integer({ min: -3, max: 3 })),
});
export type RunnerEffect = Infer<typeof runnerEffect>;

const runnerSpeed = number({ min: L.runnerSpeedMin, max: L.runnerSpeedMax, decimals: 2 });
const relativeSpeed = number({ min: 0.25, max: 3, decimals: 2 });
const inset = number({ min: 0, max: 0.45, decimals: 3 });

export const runnerSpawn = object({
  id: id(),
  kind: oneOf(RUNNER_OBJECT_KINDS),
  /** Template layers (`template: true`) the instances copy; one is picked per spawn with the instance RNG. */
  templates: array(id(), { min: 1, max: L.runnerTemplatesPerSpawn, unique: true }),
  /** Time between two spawns of this rule, picked in [min, max] ms with the instance RNG. */
  everyMs: object({
    min: integer({ min: L.runnerSpawnMinMs, max: L.durationMaxMs }),
    max: integer({ min: L.runnerSpawnMinMs, max: L.durationMaxMs }),
  }),
  /** Run time window (ms since the run started) in which the rule spawns. */
  startMs: optional(integer({ min: 0, max: L.durationMaxMs })),
  endMs: optional(integer({ min: 1, max: L.durationMaxMs })),
  /** Only while one of these segments is current (absent = every segment). */
  segments: optional(array(id(), { min: 1, max: L.runnerSegments, unique: true })),
  /** Instances of this rule alive at once; a spawn beyond it is skipped (never queued). */
  maxActive: integer({ min: 1, max: L.runnerActiveMax }),
  /** Speed relative to the world (1 = moves with the ground). */
  speed: optional(relativeSpeed),
  effect: runnerEffect,
});
export type RunnerSpawn = Infer<typeof runnerSpawn>;

export const runnerSegment = object({
  id: id(),
  /** Authoring label ("Ciudad"); never compiled. */
  name: optional(string({ min: 1, max: 60 })),
  durationMs: integer({ min: 1_000, max: L.durationMaxMs }),
  /** World speed multiplier while this segment is current (progression). */
  speed: optional(relativeSpeed),
  /** Scenery layers shown only while this segment is current (cross-faded): backgrounds, sky, tint. */
  show: array(id(), { max: L.runnerSceneryPerSegment, unique: true }),
});
export type RunnerSegment = Infer<typeof runnerSegment>;

export const runnerDecl = object({
  player: object({
    /** An ordinary layer of the game scene (an image the person can replace); never drawn by the runtime. */
    element: id(),
    /** Parabolic jump: apex at `height` × the player's height in the active format, back on the ground after `durationMs`. */
    jump: object({
      height: number({ min: 0.25, max: 6, decimals: 2 }),
      durationMs: integer({ min: L.runnerJumpMinMs, max: L.runnerJumpMaxMs }),
    }),
    /** Duck: the hit box keeps `height` × its height (bottom anchored) for at least `durationMs`, longer while held. */
    duck: optional(
      object({
        height: number({ min: 0.2, max: 0.9, decimals: 2 }),
        durationMs: integer({ min: 150, max: L.runnerJumpMaxMs }),
      })
    ),
    /** Hit box inset on each side, as a fraction of the player's width (x) and height (y). */
    hitbox: optional(object({ x: inset, y: inset })),
    /** After a hit, further hazards pass through for this long. */
    invulnerableMs: integer({ min: 0, max: 5_000 }),
    /** Declared element states shown in each pose (e.g. a duck pose image the person uploaded). */
    states: optional(object({ jump: optional(id()), duck: optional(id()), hit: optional(id()) })),
  }),
  /** World speed in px/s of the master format; `speedByLayout` gives other formats their own value. */
  speed: runnerSpeed,
  speedByLayout: optional(record(runnerSpeed, { max: L.layouts })),
  /** "left": the world moves left (the player runs to the right). */
  direction: oneOf(["left", "right"]),
  /** Background / foreground layers moving with the world at `factor` × its speed; `loop` repeats them seamlessly. */
  layers: optional(
    array(object({ element: id(), factor: number({ min: 0, max: 2, decimals: 3 }), loop: boolean() }), {
      max: L.runnerLayers,
    })
  ),
  /** Environment segments in order; the course is their total duration. */
  segments: array(runnerSegment, { min: 1, max: L.runnerSegments }),
  /** Cross-fade between the scenery of two segments. */
  fadeMs: integer({ min: 0, max: 3_000 }),
  /** Layers placed in the scene that scroll in with the ground (static course pieces). */
  items: optional(
    array(object({ element: id(), kind: oneOf(RUNNER_OBJECT_KINDS), effect: runnerEffect }), {
      max: L.runnerItems,
    })
  ),
  spawns: optional(array(runnerSpawn, { max: L.runnerSpawns })),
  /** Energy / health: a declared number variable with min and max; reaching its minimum loses. */
  energy: optional(object({ var: id(), drainPerSecond: optional(integer({ min: 0, max: 100 })) })),
  /** On-screen controls (layers of the game scene); the keyboard works as well (Space/↑ jump, ↓ duck). */
  controls: optional(object({ jump: optional(id()), duck: optional(id()) })),
});
export type RunnerDecl = Infer<typeof runnerDecl>;

export const GAME_OUTCOMES = ["win", "lose", "timeout", "neutral"] as const;
export type GameOutcome = (typeof GAME_OUTCOMES)[number];

export const gameDecl = object({
  id: id(),
  name: optional(string({ min: 1, max: 120 })),
  /** Scenes where the game is played; leaving them pauses a running game. */
  scenes: array(id(), { min: 1, max: L.gameScenes, unique: true }),
  /** onEnter = starts when a game scene becomes active; action = waits for a game.start action. */
  start: oneOf(["onEnter", "action"]),
  /** Number variable holding the score (written by the game and by rules). */
  score: optional(id()),
  /** Number variable holding the lives; reaching 0 loses. */
  lives: optional(id()),
  /** Time limit: this timer starts with the game; its last elapse ends the game. */
  timer: optional(id()),
  win: union("when", {
    /** Every objective found / matched / placed. */
    cleared: object({ when: literal("cleared") }),
    /** Score reaches `atLeast`: immediately, or checked when the game ends (time out, game.end auto). */
    score: object({
      when: literal("score"),
      atLeast: integer({ min: 1, max: 1_000_000 }),
      immediate: boolean(),
    }),
    /** Still playing when the time limit ends. */
    survive: object({ when: literal("survive") }),
    /** Only rules decide (game.end). */
    manual: object({ when: literal("manual") }),
    /** Runner (7.6F.1): reach the end of the course (all segments). */
    finish: object({ when: literal("finish") }),
  }),
  scoring: object({ hit: points, miss: points, missCostsLife: boolean() }),
  /** Scene shown after each outcome (timeout falls back to lose), after `delayMs`. */
  results: object({
    win: optional(id()),
    lose: optional(id()),
    timeout: optional(id()),
    delayMs: integer({ min: 0, max: 10_000 }),
  }),
  targets: optional(array(gameTarget, { max: L.gameTargets })),
  pairs: optional(gamePairs),
  drops: optional(gameDrops),
  /** Side-scrolling runner / platformer (7.6F.1, additive). */
  runner: optional(runnerDecl),
});
export type GameDecl = Infer<typeof gameDecl>;

// ─── Fallback, meta, document ───────────────────────────────────────────────

export const fallbackSpec = object({
  perLayout: record(
    object({
      source: oneOf(["generated", "uploaded"]),
      asset: id(),
      generatedFrom: optional(object({ sceneId: id(), atMs: ms })),
    }),
    { min: 1, max: L.layouts }
  ),
  linkId: id(),
  alt: string({ min: 1, max: 300 }),
});
export type FallbackSpec = Infer<typeof fallbackSpec>;

export const meta = object({
  intendedProduct: oneOf(CREATIVE_PRODUCTS),
  locale: string({ min: 2, max: 35, pattern: /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, patternName: "BCP 47" }),
  title: optional(string({ min: 1, max: 120 })),
  accessibility: object({ label, description: optional(string({ min: 1, max: 500 })) }),
});

export const creativeDocument = object({
  kind: literal(CREATIVE_DOCUMENT_KIND),
  schemaVersion: literal(CREATIVE_DOCUMENT_SCHEMA_VERSION),
  meta,
  /** Quarantined, immutable HTML5 files of this version; execution remains blocked. */
  html5: optional(html5Project),
  layouts: array(layout, { min: 1, max: L.layouts }),
  scenes: array(scene, { min: 1, max: L.scenes }),
  initialSceneId: id(),
  overlay: optional(
    object({ below: array(id(), { max: L.childrenPerContainer }), above: array(id(), { max: L.childrenPerContainer }) })
  ),
  elements: array(element, { max: L.elements }),
  assets: array(assetRef, { max: L.assets }),
  fonts: optional(array(fontRef, { max: L.fonts })),
  links: array(link, { max: L.links }),
  variables: optional(array(variableDecl, { max: L.variables })),
  timers: optional(array(timerDecl, { max: L.timers })),
  rules: optional(array(rule, { max: L.rules })),
  events: optional(array(customEventDecl, { max: L.customEvents })),
  survey: optional(surveyInstrument),
  forms: optional(array(formDecl, { max: L.forms })),
  /** Declared advergame (7.6F); absent = no game module (rule-only games remain valid). */
  game: optional(gameDecl),
  /**
   * Optional (ADR-0042): when present it is validated and served if the interactive ad cannot run;
   * when absent such an instance renders nothing and records no impression.
   */
  fallback: optional(fallbackSpec),
});

/** The canonical Creative Document v2 (validated shape). */
export type CreativeDocument = Infer<typeof creativeDocument>;

/** Used by structural parsing of a single sink (re-exported for operations). */
export type { IssueSink };
